import React from 'react';
import {Alert, Box, Button, Chip, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {CARD_SX, Eyebrow} from '../interventions/v2/primitives.js';
import {useGetRecapQuery, useGetSimulationQuery} from '../../../modules/simulations/simulations.api';
import VerdictSimulation from './VerdictSimulation';
import PiluleDistances from './PiluleDistances';
import PiluleErreur from './PiluleErreur';
import BlocEtalons from './BlocEtalons';
import {actionRecap409, cartesSemaines, messageErreurRecap, totauxRecap, verdictRecap} from './recapRules';

const ENTETES = ['Opérateur', 'Travaillées', 'Payées', 'HN', 'H25', 'H50', 'Nuits', 'Forfait', 'Coût heures'];

/** Cellule de coût : le `detail` de l'API en infobulle, pour que le CG refasse le calcul à la main. */
const CelluleCout = ({valeur, detail}) => (
    <TableCell align="right">
        <Tooltip title={detail || ''} arrow disableHoverListener={!detail}>
            <span style={{borderBottom: detail ? '1px dotted #8A929C' : 'none', cursor: detail ? 'help' : 'default'}}>{valeur}</span>
        </Tooltip>
    </TableCell>
);

/** Vue « Récap » d'un scénario terminé : bandeau coûts, une carte par semaine (table par opérateur), totaux de campagne. */
function RecapCampagne({simulationId, onRejouer}) {
    const {data: recap, isLoading, error} = useGetRecapQuery(simulationId);
    const {data: simulation} = useGetSimulationQuery(simulationId); // déjà en cache : erreur mesurée et patrimoine pour les étalons
    if (isLoading) return <Skeleton variant="rectangular" height={240} sx={{mt: 2}}/>;
    if (error || !recap) return <Alert severity="warning" sx={{mt: 2}} data-testid="recap-erreur">{messageErreurRecap(error)}
        {error?.status === 409 && onRejouer && <Button color="primary" size="small" sx={{ml: 2}} onClick={onRejouer}>{actionRecap409(messageErreurRecap(error))}</Button>}</Alert>;

    const totaux = totauxRecap(recap);
    const verdict = verdictRecap(recap);
    return (
        <Box data-testid="recap" sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
            <VerdictSimulation verdict={verdict}/>
            <PiluleDistances distances={recap.distances}/>
            {simulation && <PiluleErreur erreurMesuree={simulation.erreurMesuree} patrimoineId={simulation.patrimoine?.id}/>}

            {cartesSemaines(recap).map((s) => (
                <Box key={s.cle} sx={{...CARD_SX, p: 2}} data-testid="recap-semaine">
                    <Eyebrow sx={{mb: 1}}>{s.titre}</Eyebrow>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small" aria-label={`opérateurs ${s.titre}`}>
                            <TableHead><TableRow>{ENTETES.map((h) => <TableCell key={h} align={h === 'Opérateur' ? 'left' : 'right'}>{h}</TableCell>)}</TableRow></TableHead>
                            <TableBody>
                                {s.operateurs.map((o) => (
                                    <TableRow key={o.cle}>
                                        <TableCell>{o.operateur}</TableCell>
                                        <TableCell align="right">{o.travaillees}</TableCell>
                                        <TableCell align="right">{o.payees}{o.plancher && <Chip size="small" label={o.plancher} sx={{ml: 1, bgcolor: '#FDEBD8', color: '#B8641A', fontWeight: 700}}/>}</TableCell>
                                        <TableCell align="right">{o.hn}</TableCell>
                                        <TableCell align="right">{o.h25}</TableCell>
                                        <TableCell align="right">{o.h50}</TableCell>
                                        <TableCell align="right">{o.nuits}</TableCell>
                                        <CelluleCout valeur={o.forfait} detail={o.tipForfait}/>
                                        <CelluleCout valeur={o.coutHeures} detail={o.tipHeures}/>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={7}>Kilométrage · {s.km.libelle}{s.trajets ? ` · ${s.trajets}` : ''}</TableCell>
                                    <TableCell align="right">Coût km</TableCell>
                                    <CelluleCout valeur={s.km.cout} detail={s.km.detail}/>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ))}

            <BlocEtalons patrimoineId={simulation?.patrimoine?.id}/>

            {totaux && <Box sx={{...CARD_SX, p: 2}} data-testid="recap-totaux">
                <Eyebrow sx={{mb: 1}}>Totaux de la campagne</Eyebrow>
                <Typography sx={{fontWeight: 600}}>
                    {totaux.travaillees} travaillées · {totaux.payees} payées · HN {totaux.hn} · H25 {totaux.h25} · H50 {totaux.h50} · {totaux.nuits} nuits · forfaits {totaux.forfaits}
                </Typography>
                <Typography sx={{fontWeight: 600}}>
                    Heures {totaux.coutHeures} · km {totaux.km} ({totaux.coutKm}) · total {totaux.coutTotal}
                </Typography>
            </Box>}
        </Box>
    );
}

export default RecapCampagne;
